import type { Product } from '@scayle/storefront-core'
import { useVuelidate } from '@vuelidate/core'
import {
  type QuestionaireResultItem,
  QuestionaireBadgeItem,
} from '~/constants/questionaire'

import type {
  SbQuestionairePayload,
  SbQuestionairePayloadValue,
  SbQuestionnaire,
  SbQuestionnaireStep,
} from '~/storyblok/types/storyblok'
import useUiState from '~/composables/ui/useUiState'

type AnswerId = {
  question: string
  answer: string[]
  interpretation_type: 'value' | 'count'
}

type ResultMapping = {
  answer_ids: AnswerId[]
  result_type: string
  result_product_main_1: string
  result_product_main_2: string
  result_product_main_3: string
  result_product_complimentary_1: string
  result_product_complimentary_2: string
  // deprecated structure
  best_price_value_product?: string
  best_seller_product?: string
  premium_product?: string
  recommended_care_product?: string
  recommended_eye_solution?: string
}

type AsyncDataResponse = {
  results: ResultMapping[]
}

type EmitFunction = (event: string, payload?: unknown) => void

export function useQuestionnaire(questionnaireProps: SbQuestionnaire) {
  const { trackCTAClick, trackButtonClick, trackFormSubmit } =
    useTrackingEvents()
  const { $validation, $i18n, $fimLocalePath } = useNuxtApp()
  const { logger } = useLogging()
  const { getAppointmentUrl } = useAppointments({ lazy: true })

  // State
  const isOpen = ref(false)
  const isExpanded = ref(false)
  const loading = ref(false)
  const selectedProduct = ref<Product>()
  const items = ref<Array<QuestionaireResultItem> | undefined>()
  const recommendations = ref<Product[] | undefined>()
  const currentQuestionIndex = ref(0)
  const resultScreen = ref()

  const { setOverlayState } = useUiState()

  const hasResults = computed(() => !!items.value?.length)

  // Get questions from the blok
  const questions = computed<SbQuestionnaireStep[]>(
    () => questionnaireProps?.Questions || [],
  )

  const currentQuestionId = computed(
    () => questions.value?.[currentQuestionIndex.value]?.question_id,
  )

  const initPayload = () => {
    const payload: SbQuestionairePayload = {}
    for (const question of questions.value) {
      if (question.question_type === 'multi-select') {
        const result: Record<string, boolean> = {}
        for (const answer of question?.answers ?? []) {
          result[answer.answer_id] = false
        }

        payload[question.question_id] = result
      }

      if (question.question_type === 'single-select') {
        payload[question.question_id] = ''
      }
    }

    return payload
  }

  const payload = useSessionStorage(
    `questionaire-${questionnaireProps._uid}`,
    initPayload(),
  )

  const isSelected = (questionId: string, answerId: string) => {
    const p = toValue(payload)
    if (!p[questionId]) {
      return false
    }

    if (typeof p[questionId] === 'string') {
      return p[questionId] === answerId
    }

    return !!p?.[questionId]?.[answerId]
  }

  const rules = computed(() => {
    const questions = questionnaireProps?.Questions || []
    return Object.fromEntries(
      questions.map((question) => [
        question.question_id,
        {
          required: $validation.rule.required,
        },
      ]),
    )
  })

  useVuelidate(rules.value, payload, { $scope: false })

  const fetchJson = useRpcCall('getJsonAsset')

  // Fetch the results mapping asset
  const { data: resultMapping, refresh: fetchMapping } =
    useAsyncData<AsyncDataResponse>(
      'form-results-' + questionnaireProps._uid,
      async () => {
        // Find the first result in the blok's Results array
        const result = questionnaireProps.Results?.[0]
        if (!result?.results_mapping?.filename) {
          throw new Error('No results mapping found')
        }

        // Fetch the asset data
        return (await fetchJson({
          url: result.results_mapping.filename,
        })) as AsyncDataResponse
      },
      {
        lazy: true,
      },
    )

  const fetchProducts = useRpcCall('getProductsByReferenceKeys')

  // Check if current question is the last one
  const isLastQuestion = computed(
    () => currentQuestionIndex.value === questions.value.length - 1,
  )

  // Check if user can proceed to next question
  const canProceed = computed(
    () => !!toValue(payload)?.[currentQuestionId.value],
  )

  const selectedProductId = computed(() => {
    const product = selectedProduct.value
    if (product) {
      return `Consultant | ${getProductName(product)} | ${product.id}`
    }
    return undefined
  })

  const localizedDetailLink = computed(() => {
    if (selectedProduct.value) {
      return $fimLocalePath(
        getProductDetailRoute(selectedProduct.value, { consultant: 1 }),
      )
    }

    return undefined
  })

  const localizedAppointmentLink = computed(() =>
    getAppointmentUrl(ServiceCategoryDto.CL_CF),
  )

  // Helper functions
  const compareCount = (
    value: SbQuestionairePayloadValue,
    answers: AnswerId['answer'],
  ) => {
    if (!value) {
      return false
    }
    const count =
      typeof value === 'string'
        ? 1
        : Object.values(value).filter(Boolean).length

    return answers.some((answerValue) => {
      const [min, max] =
        answerValue
          ?.split('-')
          ?.map((num) => parseInt(num, 10))
          ?.filter((num) => !isNaN(num)) ?? []

      if (typeof min === 'undefined' && typeof max === 'undefined') {
        return false
      }
      if (typeof max === 'undefined') {
        return count === min
      }

      return min <= count && count <= max
    })
  }

  const compareValue = (
    value: SbQuestionairePayloadValue,
    answers: AnswerId['answer'],
  ) => {
    if (!value) {
      return false
    }

    const values =
      typeof value === 'string'
        ? [value]
        : Object.entries(value)
            .filter(([, value]) => !!value)
            .map(([key]) => key)

    return answers.every((answerValue) => values.includes(answerValue))
  }

  const comparePayload = (
    payload: MaybeRefOrGetter<SbQuestionairePayload>,
    result: ResultMapping,
  ) =>
    result.answer_ids.every((answerId) => {
      if (answerId.interpretation_type === 'count') {
        return compareCount(
          toValue(payload)[answerId.question],
          answerId.answer,
        )
      } else if (answerId.interpretation_type === 'value') {
        return compareValue(
          toValue(payload)[answerId.question],
          answerId.answer,
        )
      }

      return false
    })

  // Actions
  const close = (emit?: EmitFunction) => {
    emit?.('click:close')
    selectedProduct.value = undefined
    currentQuestionIndex.value = 0
    isOpen.value = false
  }

  const open = (event: MouseEvent, emit?: EmitFunction) => {
    emit?.('click:open')
    isOpen.value = true
    onCTAClick(event)
  }

  const restart = (event: MouseEvent, emit?: EmitFunction) => {
    emit?.('restart')
    selectedProduct.value = undefined
    currentQuestionIndex.value = 0
    isExpanded.value = false
    if (resultScreen.value) {
      resultScreen.value.reset()
    }
    onButtonClick(event)
  }

  const edit = (index: number, emit?: EmitFunction) => {
    emit?.('edit', questions.value[index])
    selectedProduct.value = undefined
    currentQuestionIndex.value = index
    isExpanded.value = false
    if (resultScreen.value) {
      resultScreen.value.reset()
    }
  }

  const selectProduct = (item: QuestionaireResultItem, emit?: EmitFunction) => {
    emit?.('select:product', item)
    selectedProduct.value = item.product
  }

  const goToPreviousQuestion = () => {
    if (isExpanded.value) {
      isExpanded.value = false
      return
    }

    if (currentQuestionIndex.value) {
      currentQuestionIndex.value--
      return
    }

    close()
  }

  const submit = async (event: Event, emit?: EmitFunction) => {
    event.preventDefault()

    trackFormSubmit({
      identifier: `Consultant | Question | ${currentQuestionId.value}`,
    })
    if (!isLastQuestion.value) {
      currentQuestionIndex.value++
      return
    }

    loading.value = true

    if (!resultMapping.value?.results) {
      await fetchMapping({ dedupe: 'defer' })
    }

    if (!resultMapping.value?.results) {
      // No Resultmapping
      logger.warn('Questionaire: no result mapping available', {
        payload,
      })
      loading.value = false
      isExpanded.value = true
      return
    }

    const result = resultMapping.value?.results?.find(
      (mapping: ResultMapping) => {
        return comparePayload(payload, mapping)
      },
    )

    if (!result) {
      // No result found for form options
      await logger.warn('Questionaire: no result found in mapping', {
        payload,
      })
      loading.value = false
      isExpanded.value = true
      return
    }

    const referenceKeys = [
      result.result_product_main_1,
      result.result_product_main_2,
      result.result_product_main_3,
      result.result_product_complimentary_1,
      result.result_product_complimentary_2,
      result.best_price_value_product,
      result.best_seller_product,
      result.premium_product,
      result.recommended_care_product,
      result.recommended_eye_solution,
    ].filter(Boolean)

    items.value = []
    try {
      const productResponse = await fetchProducts({
        with: SAFE_PRODUCT_WITH,
        referenceKeys,
      })

      const productMap: Record<string, Product> = Object.fromEntries(
        productResponse.map((product) => [product.referenceKey, product]),
      )

      // Definiere Struktur für Produkt-Kategorie-Zuordnungen
      const productMappings = [
        {
          id: result.result_product_main_1,
          badge: QuestionaireBadgeItem.First,
          target: 'products',
        },
        {
          id: result.result_product_main_2,
          badge: QuestionaireBadgeItem.Second,
          target: 'products',
        },
        {
          id: result.result_product_main_3,
          badge: QuestionaireBadgeItem.Third,
          target: 'products',
        },
        {
          id: result.result_product_complimentary_1,
          target: 'recommendations',
        },
        {
          id: result.result_product_complimentary_2,
          target: 'recommendations',
        },

        // deprecated labels
        {
          id: result.best_price_value_product,
          badge: QuestionaireBadgeItem.BestPrice,
          target: 'products',
        },
        {
          id: result.best_seller_product,
          badge: QuestionaireBadgeItem.BestSeller,
          target: 'products',
        },
        {
          id: result.premium_product,
          badge: QuestionaireBadgeItem.Premium,
          target: 'products',
        },
        {
          id: result.recommended_care_product,
          target: 'recommendations',
        },
        {
          id: result.recommended_eye_solution,
          target: 'recommendations',
        },
      ]

      items.value = []
      recommendations.value = []

      // Assign products to their respective arrays
      for (const mapping of productMappings) {
        if (mapping.id && productMap[mapping.id]) {
          if (mapping.target === 'products') {
            items.value.push({
              type: mapping.badge,
              badge: $i18n.t(`contact_lens_consultant.badges.${mapping.badge}`),
              product: productMap[mapping.id],
            })
          } else {
            recommendations.value.push(productMap[mapping.id])
          }
        }
      }

      emit?.('show:result', items.value)
    } catch (error) {
      // Failed loading products
      await logger.warn(error)
      return
    } finally {
      loading.value = false
      isExpanded.value = true
    }
  }

  const onButtonClick = (event: MouseEvent) => {
    const element = event.currentTarget as HTMLButtonElement
    trackButtonClick(element)
  }

  const onCTAClick = (event: MouseEvent) => {
    trackCTAClick({
      element: event.currentTarget as HTMLButtonElement,
    })
  }

  onBeforeUnmount(() => setOverlayState(false))

  return {
    // State
    isOpen,
    isExpanded,
    loading,
    selectedProduct,
    items,
    recommendations,
    currentQuestionIndex,
    resultScreen,
    hasResults,
    questions,
    currentQuestionId,
    payload,
    isLastQuestion,
    canProceed,
    selectedProductId,
    localizedDetailLink,
    localizedAppointmentLink,

    // Functions
    isSelected,
    close,
    open,
    restart,
    edit,
    selectProduct,
    goToPreviousQuestion,
    submit,
    onButtonClick,
    onCTAClick,
  }
}
